// =============================
// File: src/App.jsx
// =============================
import React, { useState, useEffect, useLayoutEffect } from "react";
import { HashRouter as Router, Route, Routes, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";

// Keep your existing pages
import MainUser from "./user/MainUser";
import MainDriver from "./driver/MainDriver";
import MainAdmin from "./admin/MainAdmin";
import MainProvider from "./provider/MainProvider";
import MainFinalClient from "./finalClient/MainFinalClient";
import ConnectionHandler from "./ConnectionHandler";
import useTokenRefresh from "./useTokenRefresh";
import ProductChat from "./finalClient/chatbot/ProductChat";


// New components below (inline for clarity). If you prefer, split them into their own files.
import FastMaxLanding from "./web/FastMaxLanding"; // <-- The landing you already have
import Login from "./web/Login";
import Register from "./web/Register";
import TermsAndConditions from "./web/TermsAndConditions";
import PrivacyPolicy from "./web/PrivacyPolicy";

const API = process.env.REACT_APP_API_URL || "";

function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useLayoutEffect(() => {
    // If navigating to an anchor like /terms#contraentrega, scroll to that id.
    if (hash) {
      const id = hash.replace(/^#/, "");
      // Use a microtask so the DOM is ready before scrolling.
      queueMicrotask(() => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "auto", block: "start" });
        } else {
          window.scrollTo({ top: 0, left: 0, behavior: "auto" });
        }
      });
      return;
    }
    // No hash: always start at top for every route change.
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname, hash]);

  return null;
}

export default function App() {
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
  const [userId, setUserId] = useState(null);
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [role, setRole] = useState("");
  const [addresses, setAddresses] = useState([]);
  const [providerId, setProviderId] = useState(null);
  

  const handleLoginSuccess = (user_id, username, email, phone_number, role, addresses = [], provider_id) => {
    setIsUserLoggedIn(true);
    setUserId(user_id);
    setUsername(username);
    setEmail(email);
    setPhoneNumber(phone_number);
    setRole(role);
    setAddresses(addresses || []);
    setProviderId(provider_id)
  };

  const handleUpdateUser = async () => {
    if (!userId) return;
    const response = await axios.get(`${API}/api/user/${userId}`);
    setUsername(response.data.username);
    setEmail(response.data.email);
    setPhoneNumber(response.data.phone_number);
    setRole(response.data.role);
    setProviderId(response.data.provider_id)
  };

  const handleLogout = () => {
    setIsUserLoggedIn(false);
    localStorage.clear();
  };

  // Restore session on mount
  useEffect(() => {
    const currentUrl = window.location.pathname;
    const isDeliveryRoute = currentUrl.includes("deliveryId");
    if (!isDeliveryRoute) {
      const token = localStorage.getItem("token");
      const storedUserId = localStorage.getItem("user_id");
      const storedUsername = localStorage.getItem("username");
      const storedEmail = localStorage.getItem("email");
      const storedPhoneNumber = localStorage.getItem("phone_number");
      const storedRole = localStorage.getItem("role");
      let storedAddresses = [];
      try { storedAddresses = JSON.parse(localStorage.getItem("addresses")) || []; } catch (_) {}

      if (token && storedUserId && storedUsername && storedEmail && storedPhoneNumber && storedRole) {
        handleLoginSuccess(
          storedUserId,
          storedUsername,
          storedEmail,
          storedPhoneNumber,
          storedRole,
          storedAddresses
        );
      }
    }
  }, []);

  useTokenRefresh(isUserLoggedIn);

  return (
    <>
      <ConnectionHandler />
      {isUserLoggedIn ? (
        <div className="container-fluid p-0">
          {role === "customer" && (
            <MainUser onLogout={handleLogout} onUpdateUser={handleUpdateUser} user_id={userId} username={username} email={email} phone_number={phoneNumber} addresses={addresses} providerId={providerId} />
          )}
          {role === "driver" && (
            <MainDriver onLogout={handleLogout} onUpdateUser={handleUpdateUser} user_id={userId} username={username} email={email} phone_number={phoneNumber} />
          )}
          {role === "admin" && (
            <MainAdmin onLogout={handleLogout} onUpdateUser={handleUpdateUser} user_id={userId} username={username} email={email} phone_number={phoneNumber} />
          )}
          {role === "provider" && (
            <MainProvider onLogout={handleLogout} onUpdateUser={handleUpdateUser} user_id={userId} username={username} email={email} phone_number={phoneNumber} />
          )}
        </div>
      ) : (
        <Router>
          <ScrollToTop />
          <Routes>
            {/* Landing always at "/" when not logged in */}
            <Route path="/" element={<FastMaxLandingEntry onLoginSuccess={handleLoginSuccess} />} />
            {/* Auth routes */}
            <Route path="/login" element={<Login onLoginSuccess={handleLoginSuccess} />} />
            <Route path="/register" element={<Register />} />
            <Route path="/terms" element={<TermsAndConditions />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            {/* Public delivery tracking page */}
            <Route path=":deliveryId" element={<MainFinalClient />} />
            <Route path="/chat/:sku/:sellerId" element={<ProductChat />} />
            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />

          </Routes>
        </Router>
      )}
    </>
  );
}

// Wrapper that renders your existing landing + wires buttons to hash routes.
function FastMaxLandingEntry({ onLoginSuccess }) {
  const navigate = useNavigate();
  return (
    <FastMaxLanding
      // Tip: update your FastMaxLanding.jsx CTA buttons to use react-router <Link to="/login"/> & <Link to="/register"/>
      // or pass handlers like below if you expose props in that component
      // onOpenLogin={() => navigate("/login")}
      // onOpenRegister={() => navigate("/register")}
    />
  );
}


